// @givmo/compliance — sending a signed filing: where it stands, and its words (CCM-R1-SUBMIT-WEB;
// UX slice 3, frames 60–73 and 83, approved under D-CCM-27).
//
// Pure: what the send review and the filing panel show, from a decoded submission read. Every
// word here is slice 3's approved frames' (DESIGN-NOTE-3 §13, as the frames changed it), a
// mechanical variant confirmed at the design GO (M1–M8) or on the renders, or one of this cut's new
// lines, approved on its renders (N3–N7, D-CCM-33). The readiness and refusal lines, the email's words, the act's statement, the
// factual line and the sending record's sign-in words are the server's, shown as served.
//
// ⚠ NOTHING HERE CLAIMS DELIVERY, RECEIPT OR ACCEPTANCE. "Sent" is Givmo's email service taking the
// email for delivery; the sending record says what it doesn't show.
//
// CCM-R1-SUBMIT-HANDOFF (frames 78–81): the organization sending a filing itself — the agency's
// instructions as its sources and its own form say them, and the organization's own record of what
// it sent. Its words are frames 78–81's (approved under D-CCM-27), the mechanical variants MH1–MH12
// (the cut's GO), the agency's own words, each quoted with where it is printed, and N-H1's way back
// to Givmo's email (approved as written, D-CCM-40).
//
// CCM-R1-SUBMIT-MCP (frames 62, 74–77, 75p and 83, approved under D-CCM-27 item 5): the
// organization's AI. A person authorizes the exact package in Givmo and may let their own AI send
// it once, before its date; the AI never changes the package or makes an authorization. Frames'
// words are used exactly; the lines on no frame (another viewer's line, the AI's send under way,
// the forms where the host can't name the person, the one-file and test-address variants) are this
// cut's W-a–W-f, approved as drafted on the cut's renders (D-CCM-45, 2026-10-07).

import { displayArtifactSize, displayComplianceDate } from './compliance-decode';
import { evidenceAbout, wentAfterAll } from './compliance-results-model';
import {
  SOMEONE_IN_YOUR_ORGANIZATION,
  SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE,
  longDateIn,
  shortDateIn,
  timeIn,
  whenOf,
  type When,
} from './compliance-signing-model';
import { N12 } from './desk-words';
import { throughTheirAi } from './review-words';
import {
  SUBMISSION_REFUSALS,
  sentByConnector,
  type Submission,
  type SubmissionAttempt,
  type SubmissionAuthorization,
  type SubmissionChannel,
  type SubmissionChecklistEntry,
  type SubmissionDestinationCitation,
  type SubmissionFeeStatement,
  type SubmissionHandoff,
} from './compliance-submission-decode';

type PersonName = ((userId: string) => string | undefined) | undefined;

// ── Where a filing's sending stands ─────────────────────────────────────────────────────────────

/**
 * The page's state, from the case's latest authorization and its newest send:
 *
 *   • `review`: nothing is authorized in force and no send is open (never authorized, or the
 *     authorization was revoked or ran out, M8; or the filing was corrected after the agency
 *     rejected the send or asked for changes): the review's act, its holds, or a Member's line;
 *   • `authorized`: authorized and not yet sent (a send refused before it went, M4): Send it;
 *   • `sending`, `sent`, `refused`, `uncertain`, `notSent`: frames 65, 66, 68, 69 and 70;
 *   • `reportedSent`: a refused send, or one recorded as not sent, that its evidence says went after
 *     all ("Reported as sent", b1 and b2), whatever became of its authorization;
 *   • `unknown`: a send in a state this build doesn't know: no act, and nothing claimed.
 */
export type SubmissionStage =
  | { kind: 'review' }
  | { kind: 'authorized'; authorization: SubmissionAuthorization }
  | { kind: 'sending'; authorization: SubmissionAuthorization; attempt: SubmissionAttempt }
  | { kind: 'sent'; authorization: SubmissionAuthorization; attempt: SubmissionAttempt }
  | { kind: 'refused'; authorization: SubmissionAuthorization; attempt: SubmissionAttempt }
  | { kind: 'uncertain'; authorization: SubmissionAuthorization; attempt: SubmissionAttempt }
  | { kind: 'notSent'; authorization: SubmissionAuthorization; attempt: SubmissionAttempt }
  | { kind: 'reportedSent'; authorization: SubmissionAuthorization; attempt: SubmissionAttempt }
  | { kind: 'unknown' };

/** One authorization's sends, by their number. */
export function attemptsUnder(
  submission: Submission,
  authorizationId: string,
): SubmissionAttempt[] {
  return submission.attempts
    .filter((attempt) => attempt.authorizationId === authorizationId)
    .sort((a, b) => a.ordinal - b.ordinal);
}

/** The case's newest send, under any authorization (the read lists them oldest first). */
export function newestAttempt(submission: Submission): SubmissionAttempt | undefined {
  return submission.attempts.at(-1);
}

/**
 * Whether the filing was corrected after the agency rejected this send or asked for changes
 * (CCM-R1-RESULTS-WEB): the read composes a different package from the one the send's
 * authorization bound, an attachment changed or the forms signed again. The corrected filing goes
 * by a new authorization (contract §10), so the page opens the review for it, where its readiness
 * lines show as served: REVIEW-30 section 9's "…must be signed again…" while its forms aren't.
 * With nothing corrected yet, a7's card stays, as drawn.
 */
function correctedSinceSent(
  submission: Submission,
  authorization: SubmissionAuthorization,
  attempt: SubmissionAttempt,
): boolean {
  const state = submission.followThrough.find(
    (entry) => entry.attemptId === attempt.attemptId,
  )?.state;
  if (state !== 'rejected' && state !== 'correction_requested') return false;
  const composed = submission.package?.packageSha256;
  return composed !== undefined && composed !== authorization.packageSha256;
}

export function submissionStage(submission: Submission): SubmissionStage {
  const authorization = submission.authorization;
  if (authorization === undefined) return { kind: 'review' };
  const attempt = attemptsUnder(submission, authorization.authorizationId).at(-1);
  if (attempt !== undefined) {
    switch (attempt.state) {
      case 'unknown':
        return { kind: 'unknown' };
      case 'pending':
        return { kind: 'sending', authorization, attempt };
      case 'uncertain':
        return { kind: 'uncertain', authorization, attempt };
      case 'provider_accepted':
        return correctedSinceSent(submission, authorization, attempt)
          ? { kind: 'review' }
          : { kind: 'sent', authorization, attempt };
      default:
        break;
    }
  }
  // CCM-R1-RESULTS-WEB-REPORTED-SENT-STAYS (D-CCM-79 (j)): a refused send, or one recorded as not
  // sent, that its evidence says went after all (the resend rule's reading, `wentAfterAll`) is the
  // organization's record of a send, kept whatever became of its authorization: run out, revoked,
  // or ended by the corrected filing's. It's followed as an accepted send is, so a corrected filing
  // opens the review (F1).
  if (
    attempt !== undefined &&
    (attempt.state === 'refused' || attempt.state === 'not_sent') &&
    wentAfterAll(evidenceAbout(submission.evidence, { attemptId: attempt.attemptId }))
  ) {
    return correctedSinceSent(submission, authorization, attempt)
      ? { kind: 'review' }
      : { kind: 'reportedSent', authorization, attempt };
  }
  // A refused send or one recorded as not sent can be sent again only while the authorization is
  // in force; one whose state this build doesn't know is not in force.
  if (authorization.state !== 'active') return { kind: 'review' };
  if (attempt === undefined) return { kind: 'authorized', authorization };
  return attempt.state === 'refused'
    ? { kind: 'refused', authorization, attempt }
    : { kind: 'notSent', authorization, attempt };
}

/** Whether the stage is the organization's record of a send (the sentence leaves then, O4). */
export function isOutcome(stage: SubmissionStage): boolean {
  return (
    stage.kind === 'sending' ||
    stage.kind === 'sent' ||
    stage.kind === 'refused' ||
    stage.kind === 'uncertain' ||
    stage.kind === 'notSent' ||
    stage.kind === 'reportedSent'
  );
}

/**
 * The refusals that say Givmo won't email this filing at all, or not this case: the panel offers
 * no Review and send for them. The holds the review itself explains (an item open, too large, no
 * reply contact, signed for another way) still offer it, as frame 63 is reached.
 */
const NOT_EMAILED: ReadonlySet<string> = new Set([
  SUBMISSION_REFUSALS.caseWithdrawn,
  SUBMISSION_REFUSALS.casePredates,
  SUBMISSION_REFUSALS.filingNotOnRegister,
  SUBMISSION_REFUSALS.ruleReplaced,
  SUBMISSION_REFUSALS.periodReplaced,
  SUBMISSION_REFUSALS.alreadySent,
  SUBMISSION_REFUSALS.noEmailChannel,
  SUBMISSION_REFUSALS.formUnsigned,
  SUBMISSION_REFUSALS.formNotPrepared,
  SUBMISSION_REFUSALS.feeNotSendable,
]);

/**
 * The refusals that say Givmo won't email this filing at all while the organization can still send
 * it itself (D-CCM-26: a fee; or no email channel): the panel offers **How to send it** for them.
 */
const SENT_BY_HAND: ReadonlySet<string> = new Set([
  SUBMISSION_REFUSALS.feeNotSendable,
  SUBMISSION_REFUSALS.noEmailChannel,
]);

/**
 * The panel's buttons under its four lines (slice 3 §4): which leads, if any — **Review and send**,
 * **Check what happened**, or **How to send it** (frame 78).
 */
export type PanelSendAction = 'review' | 'check' | 'handoff' | undefined;

export function panelSendAction(submission: Submission, deskHolds = false): PanelSendAction {
  const stage = submissionStage(submission);
  if (stage.kind === 'uncertain') return 'check';
  // CCM-R1-DESK-WEB (Q12; review-143's F4): while Givmo's filing desk has the filing, the
  // organization's own ways to send are hidden, as on the send page. Saying what happened to an
  // uncertain send stays the organization's (desk-14), so Check what happened stays.
  if (deskHolds) return undefined;
  if (
    stage.kind === 'sending' ||
    stage.kind === 'sent' ||
    stage.kind === 'reportedSent' ||
    stage.kind === 'unknown'
  ) {
    return undefined;
  }
  // Once the organization recorded what it sent itself, the panel offers no send (frame 81),
  // unless an authorization is under way since: then its own state leads.
  if (stage.kind === 'review' && submission.handoffs.length > 0) return undefined;
  if (submission.package === undefined) return undefined;
  const codes = submission.readiness.map((reason) => reason.code);
  if (codes.some((code) => SENT_BY_HAND.has(code))) {
    // Givmo won't email it: the organization sends it itself, unless the case itself is in the way
    // (withdrawn, replaced, already sent, unsigned…), and only a writer is shown the way (§4).
    const inTheWay = codes.some((code) => NOT_EMAILED.has(code) && !SENT_BY_HAND.has(code));
    return !inTheWay && submission.abilities.recordHandoff ? 'handoff' : undefined;
  }
  if (submission.destination === undefined) return undefined;
  return codes.some((code) => NOT_EMAILED.has(code)) ? undefined : 'review';
}

/** The button a send action leads with (frames 60, 78 and 69's Check what happened). */
export function sendActionLabel(action: Exclude<PanelSendAction, undefined>): string {
  switch (action) {
    case 'review':
      return REVIEW_AND_SEND;
    case 'check':
      return CHECK_WHAT_HAPPENED;
    case 'handoff':
      return HOW_TO_SEND_IT;
  }
}

/**
 * The send page shows the handoff (frames 79–80) where Givmo won't email the filing, where the
 * organization asked for it (**Send it yourself instead**), or where it already recorded what it sent;
 * never over an authorization under way, whose own card and record lead the page.
 */
export function showsHandoff(submission: Submission, yourself: boolean): boolean {
  const stage = submissionStage(submission);
  if (recordedSinceTheEmail(submission, stage)) return true;
  if (stage.kind !== 'review') return false;
  if (submission.handoffs.length > 0) return true;
  if (yourself) return submission.instructions !== undefined;
  return panelSendAction(submission) === 'handoff';
}

/**
 * Whether the organization recorded what it sent itself after the email the stage shows
 * (CCM-R1-RESULTS-WEB): its record is then the case's newest send, as the organization-wide read's
 * latest says (the GO's D2), so the page leads with it (frame 81), never with the email before it,
 * whose way back would read "You can send it again" over a filing already sent. An email still
 * going, or an authorization not yet used, leads still.
 */
export function recordedSinceTheEmail(submission: Submission, stage: SubmissionStage): boolean {
  if (
    stage.kind !== 'sent' &&
    stage.kind !== 'refused' &&
    stage.kind !== 'notSent' &&
    stage.kind !== 'reportedSent'
  ) {
    return false;
  }
  const recordedAt = newestHandoff(submission)?.recordedAt;
  const committedAt = stage.attempt.committedAt;
  if (recordedAt === undefined || committedAt === undefined) return false;
  return Date.parse(recordedAt) > Date.parse(committedAt);
}

/** The lead's first line: the served reason Givmo won't email the filing (S8 or S5), if one is. */
export function handoffLeadReason(submission: Submission): string | undefined {
  return submission.readiness.find((reason) => SENT_BY_HAND.has(reason.code))?.message;
}

/** The organization's newest record of what it sent itself, if any. */
export function newestHandoff(submission: Submission): SubmissionHandoff | undefined {
  return submission.handoffs.at(-1);
}

// ── Words: the page ────────────────────────────────────────────────────────────────────────────

export const REVIEW_AND_SEND = 'Review and send';
export const CHECK_WHAT_HAPPENED = 'Check what happened';
export const DOWNLOAD_THE_PACKAGE = 'Download the package';
export const THE_PACKAGE = 'The package';
export const THE_EMAIL = 'The email';
export const EMAIL_LEAD =
  'Exactly as it goes, in plain text. Nothing is added: no tracking, no footer.';
export const OPEN = 'Open';
export const SIGNED_FORM_ROLE = 'The signed form';
export const YOUR_AUTHORIZATION = 'Your authorization';
/** A Member's card, which they can't authorize from (frame 71). */
export const AUTHORIZATION = 'Authorization';
export const AUTHORIZE_AND_SEND = 'Authorize and send';
export const EVERY_ITEM_LINE = 'Every item is attached or marked not needed.';
export const SEE_THE_CHECKLIST = 'See the checklist';
export const YOUR_SIGN_IN_EMAIL = 'Your sign-in email';
export const EMAIL_HEADER_LABELS = {
  from: 'From',
  to: 'To',
  cc: 'Cc',
  replyTo: 'Reply-To',
} as const;
export const CARD_ROW_LABELS = {
  to: 'To',
  filing: 'Filing',
  fee: 'Fee',
  signature: 'Signature',
  checklist: 'Checklist',
} as const;

export function sendToTitle(jurisdiction: string): string {
  return `Send to ${jurisdiction}`;
}

/** "These 5 files go to Maryland, exactly as listed." (its one-file form is mechanical). */
export function packageLead(count: number, jurisdiction: string): string {
  return count === 1
    ? `This file goes to ${jurisdiction}, exactly as listed.`
    : `These ${count} files go to ${jurisdiction}, exactly as listed.`;
}

export function checklistRole(item: string): string {
  return `Checklist: ${item}`;
}

/** A served SHA-256 in frame 46's idiom: its first four and last four, `7c41…0e9d`. */
export function shortDigest(sha256: string): string {
  return sha256.length > 8 ? `${sha256.slice(0, 4)}…${sha256.slice(-4)}` : sha256;
}

/** "36 pages" / "1 page". */
export function pagesWords(count: number): string {
  return count === 1 ? '1 page' : `${count} pages`;
}

/** "5 files · 3.1 MB" under the package list. */
export function filesTotalLine(count: number, bytes: number): string {
  const size = displayArtifactSize(bytes);
  const files = count === 1 ? '1 file' : `${count} files`;
  return size.length > 0 ? `${files} · ${size}` : files;
}

/** "in place of dlcharity_sos@maryland.gov", beside the sink (frame 72). */
export function inPlaceOf(destination: string): string {
  return `in place of ${destination}`;
}

// ── Words: the card's rows ─────────────────────────────────────────────────────────────────────

/**
 * The fee as the form states it: "$0" for the served amount, or the form's own answer where it
 * isn't an amount ("N/A"). Never computed: the amount is the server's decimal string.
 */
export function feeValue(fee: SubmissionFeeStatement): string {
  if (fee.written !== undefined && /[a-z]/i.test(fee.written)) return fee.written;
  if (fee.amount !== undefined) return `$${fee.amount}`;
  return fee.written ?? '';
}

/**
 * "Entered on the form (item 1) by Maya Solberg on Nov 16, 2026"; an unnamed person's line drops
 * the name, never guesses one (copy ruling item 8). The form prints its number with a stop ("1."),
 * which the sentence leaves off.
 */
export function enteredLine(
  itemNumber: string | undefined,
  name: string | undefined,
  day: string | undefined,
): string {
  const number = itemNumber?.trim().replace(/\.$/, '');
  const where =
    number !== undefined && number.length > 0
      ? `Entered on the form (item ${number})`
      : 'Entered on the form';
  const by = name !== undefined ? ` by ${name}` : '';
  const on = day !== undefined ? ` on ${day}` : '';
  return `${where}${by}${on}`;
}

/** "Signed by Tomás Rivera on Nov 16, 2026". */
export function signedByLine(name: string | undefined, day: string): string {
  return `Signed by ${name ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE} on ${day}`;
}

/** "President, as your organization states it". */
export function capacityLine(capacity: string): string {
  return `${capacity}, as your organization states it`;
}

/** "4 attached · 3 not needed" (the link follows it). */
export function checklistCountsLine(attached: number, notNeeded: number): string {
  return `${attached} attached · ${notNeeded} not needed`;
}

/** "Daniel Okonkwo", "Daniel Okonkwo and Priya Raman", "A, B and C". */
export function andList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "1 item is still open: {item}." (its several-item form is mechanical). */
export function stillOpenLine(items: readonly string[]): string {
  return items.length === 1
    ? `1 item is still open: ${items[0]}.`
    : `${items.length} items are still open: ${andList(items)}.`;
}

/** The reply block's heading: "Maryland’s replies and a copy of this email go to". */
export function repliesGoTo(jurisdiction: string): string {
  return `${jurisdiction}’s replies and a copy of this email go to`;
}

/** The read-only one-line form (frame 71), and the single choice without the profile's (M3). */
export function repliesGoToLine(jurisdiction: string, address: string): string {
  return `${repliesGoTo(jurisdiction)} ${address}.`;
}

/** Frame 73: the one choice is the person's own sign-in email. */
export function repliesGoToSignInLine(jurisdiction: string, address: string): string {
  return `${repliesGoTo(jurisdiction)} ${address}, your sign-in email.`;
}

/**
 * "{Org}’s email, from its profile" where the form's email answer came from the profile; "{Org}’s
 * email" where it was typed on the form (M2: the backend offers the form's own answer, G2).
 */
export function organizationEmailLabel(organization: string, fromProfile: boolean): string {
  return fromProfile ? `${organization}’s email, from its profile` : `${organization}’s email`;
}

/** Frame 72's sink line, at the card's top outside production. */
export function sinkLine(
  sink: string,
  destination: string,
  replyContact: string | undefined,
): string {
  return replyContact !== undefined
    ? `In this environment Givmo sends filings only to ${sink}, never to ${destination}, and sends no copy to ${replyContact}.`
    : `In this environment Givmo sends filings only to ${sink}, never to ${destination}.`;
}

/**
 * Frame 71: "Only a Compliance Owner or a Global Admin can authorize sending a filing. At {org},
 * that’s {names}." Up to three names, then "and {n} others" (slice 3 §8); with none known, the
 * first sentence stands alone.
 */
export function onlyOwnersAuthorizeLine(
  organization: string,
  names: readonly string[] | undefined,
): string {
  const first = 'Only a Compliance Owner or a Global Admin can authorize sending a filing.';
  if (names === undefined || names.length === 0) return first;
  const shown =
    names.length <= 3
      ? andList(names)
      : `${names.slice(0, 3).join(', ')} and ${names.length - 3} ${names.length - 3 === 1 ? 'other' : 'others'}`;
  return `${first} At ${organization}, that’s ${shown}.`;
}

// ── Words: sending and its outcomes (frames 65–70) ─────────────────────────────────────────────

export const NOT_SENT = 'Not sent';
export const OUTCOME_UNCERTAIN = 'Outcome uncertain';
export const WHAT_HAPPENS_NEXT = 'What happens next';
export const BACK_TO_THE_FILING = 'Back to the filing';
export const SENDING_RECORD = 'Sending record';
export const TRY_AGAIN = 'Try again';
export const SEND_IT = 'Send it';
export const REVOKE = 'Revoke';
export const IT_ARRIVED = 'It arrived';
export const IT_NEVER_ARRIVED = 'It never arrived';
export const SUPPORT_FOUND_IT = 'Givmo support found it in the email service’s records';
export const TRYING_AGAIN_LINE = 'Nothing was sent, so trying again can’t send it twice.';
export const YOU_CAN_SEND_IT_NOW = 'You can send it now. It goes as a new email.';
export const IF_IT_HAPPENS_AGAIN = 'If it happens again, write to support@givmocharitable.org.';
export const NOT_SURE_LINE =
  'Not sure? Write to support@givmocharitable.org, and Givmo will look in its email service’s records.';
// N6 (CCM-R1-SUBMIT-WEB), approved on the cut's renders (D-CCM-33).
export const ONLY_OWNERS_RECORD =
  'Only a Compliance Owner or a Global Admin can record what happened.';
export const RECORD_AS_SENT = 'Record as sent';
export const RECORD_AS_NOT_SENT = 'Record as not sent';
export const CANCEL = 'Cancel';

/** "Nov 18, 2026 at 10:04 AM PST": the time with its zone's short name, for the sending record. */
export function stampWithZone(iso: string | undefined, timeZone?: string): string {
  const { day, time } = whenOf(iso, timeZone);
  if (iso === undefined) return day;
  const zone = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
    ...(timeZone !== undefined ? { timeZone } : {}),
  })
    .formatToParts(new Date(iso))
    .find((part) => part.type === 'timeZoneName')?.value;
  return zone !== undefined ? `${day} at ${time} ${zone}` : `${day} at ${time}`;
}

export function sendingTitle(jurisdiction: string): string {
  return `Sending to ${jurisdiction}`;
}

/** Frame 65: "Started at 10:04 AM by Maya Solberg. Givmo sends it once." */
export function startedLine(time: string, name: string | undefined): string {
  return name !== undefined
    ? `Started at ${time} by ${name}. Givmo sends it once.`
    : `Started at ${time}. Givmo sends it once.`;
}

export function sentTitle(jurisdiction: string): string {
  return `Sent to ${jurisdiction}`;
}

/** Frame 66 (S23 with the time): "Sent by email to {destination} on {date} at {time}." */
export function sentByEmailLine(destination: string, when: When): string {
  return `Sent by email to ${destination} on ${when.day} at ${when.time}.`;
}

/**
 * N7 (approved, D-CCM-33): outside production the email went to the sink, so the line names it, and
 * the agency's address it stood in for.
 */
export function sentToSinkLine(sink: string, when: When, destination: string): string {
  return `Sent by email to ${sink} on ${when.day} at ${when.time} (this environment’s test address, in place of ${destination}).`;
}

/** Frame 66's second line; outside production (N7) only its first sentence: no copy went. */
export function acceptedLine(
  jurisdiction: string,
  replyContact: string | undefined,
  sinkApplied: boolean,
): string {
  if (sinkApplied || replyContact === undefined) {
    return 'Givmo’s email service accepted it for delivery.';
  }
  return `Givmo’s email service accepted it for delivery. Givmo can’t see whether it reached ${jurisdiction}’s inbox. The copy sent to ${replyContact} shows what went.`;
}

/** M5: frame 66's "What happens next", its last sentence only (Maryland's quotes wait, G3). */
export function letterLine(jurisdiction: string): string {
  return `When ${jurisdiction}’s letter arrives, attach it to the filing.`;
}

/**
 * N5 (approved, D-CCM-33): a send recorded as sent, by whom and on what basis. Outside production
 * the finding is about the email to the sink (N7).
 */
export function reconciledSentLine(
  name: string | undefined,
  day: string,
  basis: 'copy_received' | 'provider_activity',
  replyContact: string | undefined,
  sink: string | undefined,
): string {
  const who = name ?? SOMEONE_IN_YOUR_ORGANIZATION;
  if (basis === 'provider_activity') {
    return `${who} recorded on ${day} that Givmo support found it in the email service’s records.`;
  }
  return sink !== undefined
    ? `${who} recorded on ${day} that the email to ${sink} arrived.`
    : `${who} recorded on ${day} that the copy sent to ${replyContact ?? ''} arrived.`;
}

/** Frame 70 (S22/S23): a send recorded as not sent; outside production, the sink's email (N7). */
export function recordedNeverArrivedLine(
  name: string | undefined,
  day: string,
  replyContact: string | undefined,
  sink: string | undefined,
): string {
  const who = name ?? SOMEONE_IN_YOUR_ORGANIZATION;
  return sink !== undefined
    ? `${who} recorded on ${day} that the email to ${sink} never arrived.`
    : `${who} recorded on ${day} that the copy sent to ${replyContact ?? ''} never arrived.`;
}

/** Frame 68. */
export function refusedLine(when: When, jurisdiction: string): string {
  return `Givmo’s email service didn’t take the email on ${when.day} at ${when.time}, so nothing went to ${jurisdiction}.`;
}

/** "Your authorization works until Nov 25, 2026." */
export function worksUntilLine(expiresAt: string | undefined, timeZone?: string): string {
  return `Your authorization works until ${shortDateIn(expiresAt, timeZone)}.`;
}

/** Frame 69. */
export function uncertainLine(when: When): string {
  return `Givmo started sending this at ${when.time} on ${when.day}, but its email service didn’t answer, so Givmo can’t tell whether the email went. Givmo won’t send it again on its own.`;
}

/** Frame 69's question; outside production it asks about the email to the sink (N7). */
export function didItArriveLine(
  replyContact: string | undefined,
  sink: string | undefined,
): string {
  return sink !== undefined
    ? `Did the email to ${sink} arrive?`
    : `Did the copy sent to ${replyContact ?? ''} arrive?`;
}

/** Frame 69's hint: "Its subject starts “{organization}, EIN {ein}”. Check spam too." */
export function subjectHintLine(organization: string, ein: string | undefined): string {
  const start = ein !== undefined ? `${organization}, EIN ${ein}` : organization;
  return `Its subject starts “${start}”. Check spam too.`;
}

/** The three findings' dialogs (N3 and N4, D-CCM-33; 69b approved; N7's forms outside production). */
export interface FindingDialogWords {
  title: string;
  body: string;
  confirm: string;
}

export function findingDialog(
  basis: 'copy_received' | 'provider_activity' | 'not_received',
  context: {
    day: string;
    replyContact: string | undefined;
    sink: string | undefined;
    jurisdiction: string;
  },
): FindingDialogWords {
  const { day, replyContact, sink, jurisdiction } = context;
  switch (basis) {
    case 'copy_received':
      // N3 as D-CCM-33 ruled it: the copy is the organization's own Cc, so it lands in their inbox.
      // Outside production it names the sink (N7), which is no inbox of theirs.
      return {
        title: sink !== undefined ? 'The email arrived?' : 'The copy arrived in your inbox?',
        body:
          sink !== undefined
            ? `Givmo will record the email as sent on ${day}, because it reached ${sink}. This can’t be undone.`
            : `Givmo will record the email as sent on ${day}, because its copy reached ${replyContact ?? ''}. This can’t be undone.`,
        confirm: RECORD_AS_SENT,
      };
    case 'provider_activity':
      // N4 (approved, D-CCM-33).
      return {
        title: 'Givmo support found it?',
        body: `Givmo will record the email as sent on ${day}, because Givmo support found it in its email service’s records.`,
        confirm: RECORD_AS_SENT,
      };
    case 'not_received':
      // Frame 69b (approved); outside production its title names the email, not a copy (N7).
      return {
        title: sink !== undefined ? 'The email never arrived?' : 'The copy never arrived?',
        body: `Givmo will record the email as not sent. You can then send it again from this page. If you’re not sure, ask Givmo support first, so ${jurisdiction} doesn’t get it twice.`,
        confirm: RECORD_AS_NOT_SENT,
      };
  }
}

// ── Words: the sending record (frame 66) ───────────────────────────────────────────────────────

/**
 * "Authorized by Maya Solberg on Nov 18, 2026 at 10:04 AM PST. Your organization’s setting at the
 * time: no extra sign-in check. Last sign-in before authorizing: Nov 18, 2026 at 9:51 AM, with a
 * passkey." The level and the way are the record's own words (B4); each sentence the record can't
 * fill is left out, never guessed.
 */
export function authorizedRecordLine(
  authorization: SubmissionAuthorization,
  name: string | undefined,
  timeZone?: string,
): string {
  const who = name !== undefined ? `Authorized by ${name}` : 'Authorized';
  const parts = [`${who} on ${stampWithZone(authorization.authorizedAt, timeZone)}.`];
  if (authorization.identityLevelWords !== undefined) {
    parts.push(`Your organization’s setting at the time: ${authorization.identityLevelWords}.`);
  }
  if (authorization.authTime !== undefined) {
    const { day, time } = whenOf(authorization.authTime, timeZone);
    parts.push(
      authorization.signInMethodWords !== undefined
        ? `Last sign-in before authorizing: ${day} at ${time}, ${authorization.signInMethodWords}.`
        : `Last sign-in before authorizing: ${day} at ${time}.`,
    );
  }
  return parts.join(' ');
}

/** "Sent from Givmo by Maya Solberg." */
export function sentFromGivmoLine(name: string | undefined): string {
  return name !== undefined ? `Sent from Givmo by ${name}.` : 'Sent from Givmo.';
}

/** "From {org} via Givmo <filings@…> · To {to} · Cc and Reply-To {contact}", as the send went. */
export function recipientsRecordLine(attempt: SubmissionAttempt, fromDisplayName: string): string {
  const from =
    fromDisplayName.length > 0
      ? `${fromDisplayName} <${attempt.fromAddress}>`
      : attempt.fromAddress;
  const parts = [`From ${from}`, `To ${attempt.to.join(', ')}`];
  const cc = attempt.cc.join(', ');
  if (cc.length > 0 && cc === attempt.replyTo) {
    parts.push(`Cc and Reply-To ${cc}`);
  } else {
    if (cc.length > 0) parts.push(`Cc ${cc}`);
    if (attempt.replyTo !== undefined) parts.push(`Reply-To ${attempt.replyTo}`);
  }
  return parts.join(' · ');
}

/** "Package SHA-256 7c41…0e9d · Email service reference 9xQ3…" (the reference, for support). */
export function digestsRecordLine(
  packageSha256: string,
  providerMessageId: string | undefined,
): string {
  const digest = `Package SHA-256 ${shortDigest(packageSha256)}`;
  return providerMessageId !== undefined
    ? `${digest} · Email service reference ${providerMessageId}`
    : digest;
}

export function sendingRecordDenial(jurisdiction: string): string {
  return `This record shows what Givmo sent, to whom, and when Givmo’s email service accepted it. It doesn’t show that ${jurisdiction} received it or accepted the filing.`;
}

/**
 * The same line for a send a person recorded as sent ("It arrived", or support's finding): the email
 * service never answered, so the record holds no acceptance and the line names none — frame 66's
 * approved line less its absent fact (copy ruling item 8; confirmed mechanical at cut 1003-7's fold).
 */
export function recordedSendDenial(jurisdiction: string): string {
  return `This record shows what Givmo sent and to whom. It doesn’t show that ${jurisdiction} received it or accepted the filing.`;
}

// ── The panel's "Sent to {jurisdiction}" line (frame 83) ───────────────────────────────────────

/**
 * Frame 83's value for the case's newest send, or `undefined` where no send is recorded (the
 * built line stands: "Nothing recorded yet", or the register's sent date). Dates are the
 * viewer's, as the panel's other lines are.
 */
export function sentLedgerValue(
  submission: Submission,
  personName: PersonName,
): string | undefined {
  const attempt = newestAttempt(submission);
  const handoff = newestHandoff(submission);
  // The organization's own record, where it is the newest (frame 83: "…, by mail · recorded by …").
  if (handoff !== undefined && (attempt === undefined || recordedAfter(handoff, attempt))) {
    return handoffRecordedLine(handoff, personName?.(handoff.recordedByUserId));
  }
  // Frames 75p and 83: authorized for its author's AI and not sent under it yet (CCM-R1-SUBMIT-MCP).
  const authorization = submission.authorization;
  if (
    authorization !== undefined &&
    authorization.state === 'active' &&
    authorization.connectorDispatchAllowed &&
    attemptsUnder(submission, authorization.authorizationId).length === 0
  ) {
    return authorizedForAiLedgerValue(
      shortDateIn(authorization.authorizedAt),
      personName?.(authorization.authorizedByUserId),
    );
  }
  if (attempt === undefined) return undefined;
  // CCM-R1-DESK-WEB (desk-13b): Givmo's desk's send names the desk (N12), never a person.
  const name =
    attempt.dispatchedThrough === 'desk' ? N12 : personName?.(attempt.dispatchedByUserId);
  const byAi = sentByConnector(attempt);
  switch (attempt.state) {
    case 'pending': {
      const started = timeIn(attempt.committedAt);
      if (byAi) return aiSendingLedgerValue(started, name);
      return name !== undefined
        ? `Sending now · started ${started} by ${name}`
        : `Sending now · started ${started}`;
    }
    case 'provider_accepted': {
      const day = shortDateIn(attempt.committedAt);
      if (attempt.sinkApplied) return `${day}, emailed to this environment’s test address`;
      if (byAi) return emailedByAiLedgerValue(day, name);
      return name !== undefined ? `${day}, emailed by ${name}` : `${day}, emailed`;
    }
    case 'refused':
      return `Not sent · ${shortDateIn(attempt.committedAt)} · Givmo’s email service didn’t take it`;
    case 'uncertain':
      return `Outcome uncertain · ${shortDateIn(attempt.committedAt)} · not sent again`;
    case 'not_sent':
      return `Not sent · recorded ${shortDateIn(attempt.reconciledAt ?? attempt.committedAt)}`;
    default:
      return undefined;
  }
}

function instant(iso: string | undefined): number {
  const ms = iso !== undefined ? Date.parse(iso) : NaN;
  return Number.isNaN(ms) ? 0 : ms;
}

/** Whether the organization's record was made after the send. */
function recordedAfter(handoff: SubmissionHandoff, attempt: SubmissionAttempt): boolean {
  return instant(handoff.recordedAt) >= instant(attempt.committedAt);
}

// ── The handoff (CCM-R1-SUBMIT-HANDOFF; frames 78–81, approved under D-CCM-27) ─────────────────

export const HOW_TO_SEND_IT = 'How to send it';
export const HERE_IS_HOW = 'Here’s how to send it yourself.';
export const SEND_IT_YOURSELF_INSTEAD = 'Send it yourself instead';
/**
 * N-H1 (approved, D-CCM-40; CCM-R1-SUBMIT-HANDOFF-LINK): the way back from sending it yourself, at
 * the foot of step 2, for a filing Givmo can email. It opens the review, where nothing is emailed
 * until someone authorizes it.
 */
export const HAVE_GIVMO_EMAIL_IT_INSTEAD = 'Have Givmo email it instead';
export const GET_THE_PACKAGE = 'Get the package.';
export const ADD_THE_FEE = 'Add the fee.';
export const RECORD_WHAT_YOU_SENT_STEP = 'Record what you sent.';
export const RECORD_WHAT_YOU_SENT = 'Record what you sent';
export const WHO_SIGNS = 'Who signs';
export const HOW_YOU_SENT_IT = 'How you sent it';
export const DATE_SENT = 'Date sent';
export const WHAT_YOU_SENT = 'What you sent';
export const SOMETHING_DIFFERENT = 'Something different';
export const PROOF_OF_SENDING_OPTIONAL = 'Proof of sending (optional)';
export const ATTACH = 'Attach';
export const SAVE = 'Save';

/** The channels' names on step 3 (frame 79). */
export const CHANNEL_HEADINGS: Readonly<Partial<Record<SubmissionChannel, string>>> = {
  postal_mail: 'By mail',
  online_portal: 'Online',
  email: 'By email',
};

/** "Send it to Maryland." */
export function sendItToStep(jurisdiction: string): string {
  return `Send it to ${jurisdiction}.`;
}

/**
 * Step 1's lead (frame 79): "These 4 files are what Maryland’s checklist asks for, as your
 * organization prepared them." Its one-file form is mechanical, as the review's own lead's is.
 */
export function handoffPackageLead(count: number, jurisdiction: string): string {
  return count === 1
    ? `This file is what ${jurisdiction}’s checklist asks for, as your organization prepared it.`
    : `These ${count} files are what ${jurisdiction}’s checklist asks for, as your organization prepared them.`;
}

/** "Signed by Daniel Okonkwo on Nov 17, 2026 · President, as your organization states it." */
export function signedWithCapacityLine(
  name: string | undefined,
  day: string,
  capacity: string,
): string {
  const who = name ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE;
  return capacity.length > 0
    ? `Signed by ${who} on ${day} · ${capacity}, as your organization states it.`
    : `Signed by ${who} on ${day}.`;
}

/** "Maryland’s form: “This form must be signed by …”" (frame 79): the form's own words. */
export function formSaysLine(jurisdiction: string, text: string): string {
  return `${jurisdiction}’s form: “${text}”`;
}

/**
 * Step 2 (frame 79): "$200, entered on the form (item 1) by Maya Solberg on Nov 16, 2026." The
 * form prints its item number with a stop ("1."), which the sentence leaves off; an unnamed person's
 * line drops the name, never guesses one.
 */
export function feeEnteredLine(
  fee: SubmissionFeeStatement,
  name: string | undefined,
  day: string | undefined,
): string {
  const number = fee.itemNumber?.trim().replace(/\.$/, '');
  const where =
    number !== undefined && number.length > 0
      ? `entered on the form (item ${number})`
      : 'entered on the form';
  const by = name !== undefined ? ` by ${name}` : '';
  const on = day !== undefined ? ` on ${day}` : '';
  return `${feeValue(fee)}, ${where}${by}${on}.`;
}

/**
 * The fee's payment in the agency's own words (frame 79), labelled by where it is printed (MH9):
 * "Maryland’s checklist, page 2: “…”" when it is on the form's checklist page as the preparation
 * serves it, else "Maryland’s form, page {n}: “…”".
 */
export function feePaymentLine(
  jurisdiction: string,
  quote: { text: string; page?: number },
  checklistPage: number | undefined,
): string {
  const where = quote.page !== undefined && quote.page === checklistPage ? 'checklist' : 'form';
  const page = quote.page !== undefined ? `, page ${quote.page}` : '';
  return `${jurisdiction}’s ${where}${page}: “${quote.text}”`;
}

/** Under the mail address (frame 79): "Maryland’s form, page 1 — source checked on Sep 27, 2026." */
export function formSourceLine(
  jurisdiction: string,
  page: number | undefined,
  checkedOn: string | undefined,
): string {
  const where =
    page !== undefined ? `${jurisdiction}’s form, page ${page}` : `${jurisdiction}’s form`;
  return checkedOn !== undefined
    ? `${where} — source checked on ${displayComplianceDate(checkedOn)}.`
    : `${where}.`;
}

/** Frame 79, with a fee: "Maryland’s form also lists email (…), but doesn’t say how the fee is paid that way." */
export function emailWithFeeLine(jurisdiction: string, address: string): string {
  return `${jurisdiction}’s form also lists email (${address}), but doesn’t say how the fee is paid that way.`;
}

/** Step 4's words (frame 79). */
export function recordStepLine(jurisdiction: string): string {
  return `When it’s gone, record how and when. Givmo keeps this as your organization’s record of sending. It isn’t a filing Givmo sent, and it doesn’t show that ${jurisdiction} received it.`;
}

/** "Online, in Maryland OneStop" (frame 80); "Online" where no portal is named. */
export function onlineChoiceLabel(portalName: string | undefined): string {
  return portalName !== undefined ? `Online, in ${portalName}` : 'Online';
}

/** "The package above (4 files)" (frame 80); one file reads "(1 file)" (MH6). */
export function packageAboveLabel(count: number): string {
  return count === 1 ? 'The package above (1 file)' : `The package above (${count} files)`;
}

/**
 * Frame 80's hint: "For example a mailing receipt, the email you sent, or OneStop’s confirmation
 * page." It names the served portal in full (MH7), and drops that clause where none is recorded.
 */
export function proofHintLine(portalName: string | undefined): string {
  return portalName !== undefined
    ? `For example a mailing receipt, the email you sent, or ${portalName}’s confirmation page.`
    : 'For example a mailing receipt or the email you sent.';
}

/** The Sent line's way (MH8): "by mail", "by email", "online". */
const HANDOFF_WAYS: Readonly<Partial<Record<SubmissionChannel, string>>> = {
  postal_mail: 'by mail',
  email: 'by email',
  online_portal: 'online',
};

/**
 * Frame 81's Sent line: "Nov 20, 2026, by mail · recorded by Maya Solberg" (MH8 for the other ways);
 * an unnamed person's line drops the name. A record made through the person's connected AI says
 * so, in N56's form (D-CCM-96, frame A4): "… · recorded through Maya Solberg’s AI", or "through a
 * connected AI" where Givmo can't name the person.
 */
export function handoffRecordedLine(handoff: SubmissionHandoff, name: string | undefined): string {
  const way = handoff.channel !== 'unknown' ? HANDOFF_WAYS[handoff.channel] : undefined;
  const day = displayComplianceDate(handoff.sentOn);
  const head = way !== undefined ? `${day}, ${way}` : day;
  if (handoff.recordedChannel === 'connector') {
    return `${head} · recorded through ${throughTheirAi(name)}`;
  }
  return name !== undefined ? `${head} · recorded by ${name}` : `${head} · recorded`;
}

// ── The checklist a record kept ─────────────────────────────────────────────────────────────────

/** How many items a kept checklist had attached, not needed and still open. */
export function checklistRecordCounts(record: readonly SubmissionChecklistEntry[]): {
  attached: number;
  notNeeded: number;
  open: number;
} {
  return {
    attached: record.filter((entry) => entry.status === 'attached').length,
    notNeeded: record.filter((entry) => entry.status === 'not_needed').length,
    open: record.filter((entry) => entry.status === 'open').length,
  };
}

// ── The review's agency sentences (CCM-R1-SUBMIT-HANDOFF; G3, frames 62 and 66) ────────────────

/** "Official source" (built): the agency's own form, where it was taken from. */
export const OFFICIAL_SOURCE = 'Official source';

/**
 * Frame 62's To row, under the address: the form's own email line with where it is printed (its own
 * cite, MH10) and when the source was checked: "COR-92, page 1: “When emailing the form, email it to:
 * dlcharity_sos@maryland.gov.” — source checked on Sep 27, 2026." ("Official source" follows it.)
 */
export function toRowQuoteLine(
  quote: { text: string; cite: string },
  checkedOn: string | undefined,
): string {
  const quoted = quote.cite.length > 0 ? `${quote.cite}: “${quote.text}”` : `“${quote.text}”`;
  return checkedOn !== undefined
    ? `${quoted} — source checked on ${displayComplianceDate(checkedOn)}.`
    : quoted;
}

/**
 * Frame 62's To row from the citation the backend serves (CCM-ST2-SUBMIT-CITE; UX slice 3's P2): the
 * line's approved shape, with the citation of the source the sentence was read from in its slot
 * (D-CCM-34), not the form's own cite: "Maryland Secretary of State, COR-92 Registration Statement
 * for Charitable Organizations, page 1: “When emailing the form, email it to:
 * dlcharity_sos@maryland.gov.” — source checked on Sep 15, 2026." `dated` is the source line's own
 * dated wording and `change` its sentence while a possible change is under review (both
 * `compliance-shared`'s, so no line spells them twice). With no citation the sentence opens the line
 * and no page is named: a page of a source the line doesn't name says nothing. ("Official source"
 * follows it, where the source has a link.)
 */
export function toRowCitationLine(
  citation: Pick<SubmissionDestinationCitation, 'sentence' | 'page' | 'displayCitation'>,
  dated: string | undefined,
  change: string | undefined,
): string {
  const label = citation.displayCitation;
  const where =
    label === undefined
      ? undefined
      : citation.page !== undefined
        ? `${label}, page ${citation.page}`
        : label;
  const quoted =
    where !== undefined ? `${where}: “${citation.sentence}”` : `“${citation.sentence}”`;
  const line = dated !== undefined ? `${quoted} — ${dated}.` : quoted;
  return change !== undefined ? `${line} ${change}` : line;
}

/**
 * Frame 62's letters line, under the reply choice: "Maryland’s instructions say its letters go “to
 * the email address provided on the organization’s registration”. Item 9 on the form says
 * hello@larkspurreading.example." Its second sentence only where the form's email item is known.
 */
export function lettersLine(
  jurisdiction: string,
  replies: string,
  itemNumber: string | undefined,
  email: string | undefined,
): string {
  const first = `${jurisdiction}’s instructions say its letters go “${replies}”.`;
  const number = itemNumber?.trim().replace(/\.$/, '');
  if (number === undefined || number.length === 0 || email === undefined) return first;
  return `${first} Item ${number} on the form says ${email}.`;
}

/**
 * Frame 66's What happens next: "Maryland’s instructions say it will “review and respond to the
 * organization”, and that letters go “to the email address provided on the organization’s
 * registration”. When Maryland’s letter arrives, attach it to the filing." With one of its two quotes
 * served, that one's clause (MH11); with none, M5's built line.
 */
export function whatHappensNextLine(
  jurisdiction: string,
  response: string | undefined,
  replies: string | undefined,
): string {
  const last = letterLine(jurisdiction);
  if (response !== undefined && replies !== undefined) {
    return `${jurisdiction}’s instructions say it will “${response}”, and that letters go “${replies}”. ${last}`;
  }
  if (response !== undefined) {
    return `${jurisdiction}’s instructions say it will “${response}”. ${last}`;
  }
  if (replies !== undefined) {
    return `${jurisdiction}’s instructions say its letters go “${replies}”. ${last}`;
  }
  return last;
}

// ── The organization's AI (CCM-R1-SUBMIT-MCP; frames 62, 74–77, 75p and 83, approved) ─────────

/** Frame 62's link beside "Send it yourself instead", and frame 74's heading. */
export const LET_YOUR_AI_SEND_THIS = 'Let your AI send this';
/** Frame 74's act. */
export const AUTHORIZE_MY_AI_TO_SEND_IT = 'Authorize my AI to send it';
/** Frame 74's way back to frame 62's act. */
export const SEND_IT_MYSELF_INSTEAD = 'Send it myself instead';
/** Frame 75's card. */
export const AUTHORIZED_FOR_YOUR_AI = 'Authorized for your AI';
export const SEND_IT_NOW = 'Send it now';
export const WHAT_YOU_TELL_YOUR_AI =
  'What you tell your AI is kept with the sending record as your organization’s own account; Givmo doesn’t check it.';
/** Frame 76's dialog. */
export const REVOKE_THIS_AUTHORIZATION = 'Revoke this authorization?';
export const YOUR_AI_WONT_BE_ABLE =
  'Your AI won’t be able to send it. Nothing has been sent. You can authorize it again.';
export const KEEP_IT = 'Keep it';
/** Frame 77's quoted box, its last line. */
export const KEPT_AS_YOUR_ACCOUNT =
  'Givmo keeps this as your organization’s own account and doesn’t check it.';

/**
 * Frame 74: "Your AI can send exactly this email and these 5 files to Maryland, once, as you see
 * them here. It can’t change them. If anything in the package changes, it can’t send it." Its
 * one-file form is W-e (approved, D-CCM-45; CCM-R1-SUBMIT-MCP), as `packageLead`'s is mechanical.
 */
export function aiCanSendLine(count: number, jurisdiction: string): string {
  return count === 1
    ? `Your AI can send exactly this email and this file to ${jurisdiction}, once, as you see it here. It can’t change it. If anything in the package changes, it can’t send it.`
    : `Your AI can send exactly this email and these ${count} files to ${jurisdiction}, once, as you see them here. It can’t change them. If anything in the package changes, it can’t send it.`;
}

/**
 * Frame 74: "Your authorization works until November 25, 2026." — before any authorization
 * exists, so the long date of now plus the served lifetime, on the viewer's clock.
 */
export function aiWorksUntilLine(lifetimeSeconds: number, now: Date, timeZone?: string): string {
  const until = new Date(now.getTime() + lifetimeSeconds * 1000).toISOString();
  return `Your authorization works until ${longDateIn(until, timeZone)}.`;
}

/** Frame 75: "You authorized your AI to send exactly this, once, until Nov 25, 2026. Ask your AI to send it." */
export function authorizedForYourAiLine(expiresAt: string | undefined, timeZone?: string): string {
  return `You authorized your AI to send exactly this, once, until ${shortDateIn(expiresAt, timeZone)}. Ask your AI to send it.`;
}

/**
 * A person's AI, by the host's name for them; W-d (approved, D-CCM-45; CCM-R1-SUBMIT-MCP) where
 * the host can't name them.
 */
function theirAi(name: string | undefined): string {
  return name !== undefined ? `${name}’s AI` : 'a connected AI';
}

/** Frame 77: "Sent by email to {destination} on Nov 18, 2026 at 2:41 PM, by Maya Solberg’s AI." */
export function sentByAiLine(destination: string, when: When, name: string | undefined): string {
  return `Sent by email to ${destination} on ${when.day} at ${when.time}, by ${theirAi(name)}.`;
}

/**
 * W-f (approved, D-CCM-45; CCM-R1-SUBMIT-MCP): frame 77's line outside production, as N7 says
 * frame 66's: it went to the sink, in place of the agency's address.
 */
export function sentToSinkByAiLine(
  sink: string,
  when: When,
  destination: string,
  name: string | undefined,
): string {
  return `Sent by email to ${sink} on ${when.day} at ${when.time} (this environment’s test address, in place of ${destination}), by ${theirAi(name)}.`;
}

/** Frame 77's box: "Maya Solberg authorized it on Nov 18, 2026 at 10:12 AM."; W-d unnamed. */
export function aiAuthorizedOnLine(name: string | undefined, when: When): string {
  return name !== undefined
    ? `${name} authorized it on ${when.day} at ${when.time}.`
    : `It was authorized on ${when.day} at ${when.time}.`;
}

/**
 * Frame 77's box: "What Maya Solberg asked their AI, as the AI reported it: “Send the Maryland
 * registration this afternoon.”"; W-d unnamed.
 */
export function aiAskedLine(name: string | undefined, request: string): string {
  return name !== undefined
    ? `What ${name} asked their AI, as the AI reported it: “${request}”`
    : `What the person asked their AI, as the AI reported it: “${request}”`;
}

/** Frame 77's sending record: "Sent by Maya Solberg’s connected AI."; W-d unnamed. */
export function sentByConnectedAiLine(name: string | undefined): string {
  return name !== undefined ? `Sent by ${name}’s connected AI.` : 'Sent by a connected AI.';
}

/** Frames 75p and 83: "Authorized Nov 18, 2026 for Maya Solberg’s AI · not sent yet"; W-d unnamed. */
export function authorizedForAiLedgerValue(day: string, name: string | undefined): string {
  return `Authorized ${day} for ${theirAi(name)} · not sent yet`;
}

/** Frame 83: "Nov 18, 2026, emailed by Maya Solberg’s AI"; W-d unnamed. */
export function emailedByAiLedgerValue(day: string, name: string | undefined): string {
  return `${day}, emailed by ${theirAi(name)}`;
}

// Cut 1006-9's W-a–W-c, on no frame (approved, D-CCM-45; CCM-R1-SUBMIT-MCP). DESIGN-NOTE-3 §9's
// own line is W-a.

/** W-a: another viewer of an authorization its author let their AI send. */
export function aiAuthorizedByLine(
  name: string | undefined,
  expiresAt: string | undefined,
  timeZone?: string,
): string {
  const who = name ?? SOMEONE_IN_YOUR_ORGANIZATION;
  return `${who} authorized their AI to send this, until ${shortDateIn(expiresAt, timeZone)}.`;
}

/** W-b: frame 65's line, the AI's send under way. */
export function aiStartedLine(time: string, name: string | undefined): string {
  return `Started at ${time} by ${theirAi(name)}. Givmo sends it once.`;
}

/** W-c: the panel's line meanwhile. */
export function aiSendingLedgerValue(time: string, name: string | undefined): string {
  return `Sending now · started ${time} by ${theirAi(name)}`;
}
